import { WarningFilled } from '@ant-design/icons';
import {
  EnumRichiestaDTOStato,
  EnumRichiestaDTOTipoIntegrazione,
  RichiestaDTO,
  RichiestaDTOStato,
  TipoDomandaDTO,
} from '@framework/api-client';
import { expectNeverAsString } from '@framework/engine';
import { t, useTranslation } from '@framework/i18n';
import { Tag, TagProps, Tooltip } from 'antd';
import styled, { DefaultTheme, useTheme } from 'styled-components';
import { SubmissionBurdenLogic } from '../../burden/SubmissionBurdenLogic';
import { useQuoteActionRequiredMessages } from '../../payments/quote/QuoteActionRequiredMessages';

const StyledTag = styled(Tag)<{ $mini?: boolean; $side?: 'left' | 'right' }>`
  text-transform: uppercase;
  letter-spacing: 0.03em;
  line-height: ${props => (props.$mini ? '14px' : '20px')};
  padding-inline: ${props => (props.$mini ? '4px' : '7px')};

  border-top-right-radius: ${props => (props.$side === 'left' ? '0' : '2px')};
  border-bottom-right-radius: ${props =>
    props.$side === 'left' ? '0' : '2px'};
  border-top-left-radius: ${props => (props.$side === 'right' ? '0' : '2px')};
  border-bottom-left-radius: ${props =>
    props.$side === 'right' ? '0' : '2px'};

  margin-inline-end: ${props => (props.$side == 'left' ? '0' : '8px')};
  &&& {
    font-weight: 600;
  }
`;

/**
 * Ritorna le impostazioni per il tag
 *
 */
function getTagOptions(
  state: RichiestaDTOStato | undefined,
  theme?: DefaultTheme,
): TagProps {
  if (!state) return { color: '#a5c201', children: 'N/D' };
  switch (state) {
    case EnumRichiestaDTOStato.BOZZA:
      return {
        color: '#757575',
        children: t('submission-state-tag.bozza', 'BOZZA'),
      };
    case EnumRichiestaDTOStato.PAGAMENTO:
      return {
        color: '#feaf2d',
        children: t('submission-state-tag.pagamento', 'IN PAGAMENTO'),
      };
    case EnumRichiestaDTOStato.CREATA:
      return {
        color: '#9254de',
        children: t('submission-state-tag.creata', 'CREATA'),
      };
    case EnumRichiestaDTOStato.IN_INVIO:
      return {
        color: theme?.colorInfo ?? '#569bc4',
        children: t('submission-state-tag.in-invio', 'IN INVIO'),
      };
    case EnumRichiestaDTOStato.INVIATA:
      return {
        color: theme?.colorSuccess ?? '#389e0d',
        children: t('submission-state-tag.inviata', 'INVIATA'),
      };
    case EnumRichiestaDTOStato.INTEGRATA:
      return {
        color: '#4b4f4d',
        children: t('submission-state-tag.integrata', 'INTEGRATA'),
      };
    case EnumRichiestaDTOStato.CANCELLATA:
      return {
        color: theme?.colorError ?? '#fe0000',
        children: t('submission-state-tag.cancellata', 'CANCELLATA'),
      };
    case EnumRichiestaDTOStato.RICEVUTA:
      return {
        color: theme?.colorSuccess ?? '#389e0d',
        children: t('submission-state-tag.ricevuta', 'RICEVUTA'),
      };
    case EnumRichiestaDTOStato.CONCLUSA:
      return {
        color: theme?.colorSuccessActive ?? '#227100',
        children: t('submission-state-tag.conclusa', 'CONCLUSA'),
      };
    default:
      return { children: expectNeverAsString(state) };
  }
}

export interface SubmissionStateTagProps {
  submission?: RichiestaDTO | null;
  inquiry?: TipoDomandaDTO;
  mini?: boolean;
}

/**
 * Stato di una richiesta
 */
export function SubmissionStateTag(props: SubmissionStateTagProps) {
  const { submission, mini, inquiry } = props;
  const state = submission?.stato;
  const theme = useTheme();
  const { t } = useTranslation();

  const hasPendingQuotesTooltipText =
    useQuoteActionRequiredMessages(submission);

  if (!state) return null;

  if (state === EnumRichiestaDTOStato.CREATA && inquiry?.flNonInviabile) {
    return null;
  }

  const needIntegration =
    submission?.tipoIntegrazione ===
    EnumRichiestaDTOTipoIntegrazione.RICHIESTA_INTEGRAZIONE;

  const hasPendingQuotes = SubmissionBurdenLogic.hasPendingQuotes(
    submission.statoPreventivi,
  );

  const isAlreadyIntegrated = state === EnumRichiestaDTOStato.INTEGRATA;

  const needIntegrationTooltipText = isAlreadyIntegrated
    ? t(
        'submission-state-tag.integrata-dopo-richiesta-integrazione',
        'Integrata dopo richiesta di integrazione',
      )
    : t(
        'submission-state-tag.richiesta-integrazione',
        'L’istanza è stata abilitata alla presentazione di modifiche e/o integrazioni',
      );

  return (
    <span style={{ textWrap: 'nowrap' }}>
      <StyledTag
        $mini={mini}
        $side={needIntegration || hasPendingQuotes ? 'left' : undefined}
        {...getTagOptions(state, theme)}
      />
      {needIntegration && (
        <Tooltip title={needIntegrationTooltipText}>
          <StyledTag
            $mini={mini}
            $side="right"
            color={theme?.colorWarning ?? '#faad14'}
          >
            <WarningFilled aria-label={needIntegrationTooltipText} />
          </StyledTag>
        </Tooltip>
      )}
      {hasPendingQuotes && (
        <Tooltip title={hasPendingQuotesTooltipText}>
          <StyledTag
            $mini={mini}
            $side="right"
            color={theme?.colorWarning ?? '#faad14'}
          >
            <WarningFilled aria-label={hasPendingQuotesTooltipText} />
          </StyledTag>
        </Tooltip>
      )}
    </span>
  );
}
